import { useState, useEffect, useCallback } from 'react';
import {
  ButtonVariant,
  type ButtonState,
  type ButtonSize,
  type ButtonType,
} from './types';

// Variant color configurations based on design system
export const variantColors = {
  [ButtonVariant.Primary]: {
    enabled: 'bg-primary-color-40',
    hovered: 'bg-primary-color-50',
    focused: 'bg-primary-color-40',
    pressed: 'bg-primary-color-30',
    text: 'text-neutral-100',
    focusRing: 'ring-primary-color-60',
  },
  [ButtonVariant.Secondary]: {
    enabled: 'bg-secondary-color-40',
    hovered: 'bg-secondary-color-50',
    focused: 'bg-secondary-color-40',
    pressed: 'bg-secondary-color-30',
    text: 'text-neutral-0',
    focusRing: 'ring-secondary-color-60',
  },
  [ButtonVariant.Success]: {
    enabled: 'bg-success-40',
    hovered: 'bg-success-50',
    focused: 'bg-success-40',
    pressed: 'bg-success-30',
    text: 'text-neutral-100',
    focusRing: 'ring-success-60',
  },
  [ButtonVariant.Error]: {
    enabled: 'bg-error-40',
    hovered: 'bg-error-50',
    focused: 'bg-error-40',
    pressed: 'bg-error-30',
    text: 'text-neutral-100',
    focusRing: 'ring-error-60',
  },
  [ButtonVariant.Warning]: {
    enabled: 'bg-warning-40',
    hovered: 'bg-warning-50',
    focused: 'bg-warning-40',
    pressed: 'bg-warning-30',
    text: 'text-neutral-0',
    focusRing: 'ring-warning-60',
  },
  [ButtonVariant.Information]: {
    enabled: 'bg-information-40',
    hovered: 'bg-information-50',
    focused: 'bg-information-40',
    pressed: 'bg-information-30',
    text: 'text-neutral-100',
    focusRing: 'ring-information-60',
  },
};

// Size configurations based on Figma design
export const sizeConfig = {
  XSmall: {
    paddingX: 12,
    paddingY: 8,
    fontSize: 12,
    lineHeight: 16,
    iconSize: 16,
    gap: 6,
    borderRadius: {
      Round: 8,
      Square: 8,
    },
  },
  Small: {
    paddingX: 16,
    paddingY: 10,
    fontSize: 14,
    lineHeight: 20,
    iconSize: 20,
    gap: 8,
    borderRadius: {
      Round: 12,
      Square: 12,
    },
  },
  Medium: {
    paddingX: 24,
    paddingY: 14,
    fontSize: 18,
    lineHeight: 24,
    iconSize: 24,
    gap: 12,
    borderRadius: {
      Round: 16,
      Square: 8,
    },
  },
  Large: {
    paddingX: 40,
    paddingY: 24,
    fontSize: 22,
    lineHeight: 28,
    iconSize: 32,
    gap: 14,
    borderRadius: {
      Round: 24,
      Square: 24,
    },
  },
  XLarge: {
    paddingX: 64,
    paddingY: 48,
    fontSize: 32,
    lineHeight: 40,
    iconSize: 40,
    gap: 16,
    borderRadius: {
      Round: 28,
      Square: 28,
    },
  },
};

export interface UseButtonProps {
  variant: ButtonVariant;
  buttonType: ButtonType;
  size: ButtonSize;
  state: ButtonState;
  disabled?: boolean | undefined;
  onFocus?: ((e: React.FocusEvent<HTMLButtonElement>) => void) | undefined;
  onBlur?: ((e: React.FocusEvent<HTMLButtonElement>) => void) | undefined;
  onMouseEnter?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseLeave?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseDown?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseUp?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
}

export interface ButtonStateStyles {
  backgroundColor: string;
  color: string;
  cursor: string;
  isHovered: boolean;
}

export interface UseButtonReturn {
  config: (typeof sizeConfig)[ButtonSize];
  borderRadius: number;
  isDisabled: boolean;
  stateStyles: ButtonStateStyles;
  handleMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseUp: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleFocus: (e: React.FocusEvent<HTMLButtonElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLButtonElement>) => void;
}

export const useButton = ({
  variant,
  buttonType,
  size,
  state,
  disabled,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
}: UseButtonProps): UseButtonReturn => {
  const config = sizeConfig[size];
  const borderRadius = config.borderRadius[buttonType];
  const isDisabled = disabled || state === 'Disabled';

  // Handle state transitions
  const [internalState, setInternalState] = useState<ButtonState>(state);

  useEffect(() => {
    if (state !== 'Enabled') {
      setInternalState(state);
    }
  }, [state]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Hovered');
      }
      onMouseEnter?.(e);
    },
    [isDisabled, state, onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Enabled');
      }
      onMouseLeave?.(e);
    },
    [isDisabled, state, onMouseLeave]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Pressed');
      }
      onMouseDown?.(e);
    },
    [isDisabled, state, onMouseDown]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Hovered');
      }
      onMouseUp?.(e);
    },
    [isDisabled, state, onMouseUp]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Focused');
      }
      onFocus?.(e);
    },
    [isDisabled, state, onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Enabled');
      }
      onBlur?.(e);
    },
    [isDisabled, state, onBlur]
  );

  // State-based styling
  const getStateStyles = useCallback((): ButtonStateStyles => {
    const currentStateToUse = state !== 'Enabled' ? state : internalState;
    const colors = variantColors[variant];

    if (isDisabled) {
      return {
        backgroundColor: 'bg-neutral-30',
        color: 'text-neutral-60',
        cursor: 'cursor-not-allowed',
        isHovered: false,
      };
    }

    switch (currentStateToUse) {
      case 'Hovered':
        return {
          backgroundColor: colors.enabled,
          color: colors.text,
          cursor: 'cursor-pointer',
          isHovered: true,
        };
      case 'Focused':
        return {
          backgroundColor: colors.focused,
          color: colors.text,
          cursor: 'cursor-pointer',
          isHovered: false,
        };
      case 'Pressed':
        return {
          backgroundColor: colors.pressed,
          color: colors.text,
          cursor: 'cursor-pointer',
          isHovered: false,
        };
      default:
        return {
          backgroundColor: colors.enabled,
          color: colors.text,
          cursor: 'cursor-pointer',
          isHovered: false,
        };
    }
  }, [state, internalState, variant, isDisabled]);

  const stateStyles = getStateStyles();

  return {
    config,
    borderRadius,
    isDisabled,
    stateStyles,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  };
};
